<template>
  <div class="sovi-esign-biz-page sovi-home">
    <router-link to="/" class="sovi-esign-biz-page__logo">
      <sovi-logo :size="48" />
    </router-link>

    <v-menu offset-y>
      <template #activator="{ props }">
        <v-btn
          class="language-menu"
          size="default"
          color="#eca1c2"
          v-bind="props"
        >
          <template #prepend>
            <v-icon icon="mdi-web" />
          </template>

          {{ $i18n.locale.toUpperCase() }}
        </v-btn>
      </template>

      <v-list>
        <v-list-item
          v-for="option in $i18n.availableLocales"
          :key="option"
          class="align-items-center"
          @click="onLocaleChange(option)"
        >
          <v-row align="center" no-gutters>
            {{ option.toUpperCase() }}
          </v-row>
        </v-list-item>
      </v-list>
    </v-menu>

    <sovi-content-nav />

    <!-- Hero -->
    <sovi-home-card
      background="/images/sahkoinen-allekirjoitus-5.webp"
      src="/images/sahkoinen-allekirjoitus-6.webp"
      :title="t('heroTitle')"
      fixed
      style="padding-top: 100px"
    >
      <strong>{{ t('heroSubtitle') }}</strong>

      <p>{{ t('heroText') }}</p>
    </sovi-home-card>

    <!-- Use cases -->
    <sovi-home-card
      src="/images/sahkoinen-allekirjoitus-7.webp"
      :title="t('useCasesTitle')"
      flipped
    >
      <p>{{ t('useCasesIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>
          <router-link to="/tyosopimuksen-sahkoinen-allekirjoitus">
            {{ t('useCaseEmployment') }}
          </router-link>
        </li>

        <li>
          <router-link to="/vuokrasopimuksen-sahkoinen-allekirjoitus">
            {{ t('useCaseRental') }}
          </router-link>
        </li>

        <li>{{ t('useCaseAssignment') }}</li>

        <li>{{ t('useCaseOffers') }}</li>

        <li>{{ t('useCaseRfp') }}</li>

        <li>{{ t('useCaseConsulting') }}</li>

        <li>{{ t('useCaseNda') }}</li>

        <li>{{ t('useCaseBoardMinutes') }}</li>

        <li>{{ t('useCaseAgm') }}</li>

        <li>{{ t('useCaseProcurement') }}</li>

        <li>{{ t('useCasePartnership') }}</li>

        <li>{{ t('useCasePowerOfAttorney') }}</li>

        <li>{{ t('useCaseOrderConfirmation') }}</li>

        <li>{{ t('useCaseService') }}</li>

        <li>{{ t('useCaseCustomer') }}</li>

        <li>{{ t('useCasePartner') }}</li>
      </ul>

      <p>{{ t('useCasesConclusion') }}</p>
    </sovi-home-card>

    <!-- Who is it for -->
    <sovi-home-card src="/images/content-13.webp" :title="t('whoTitle')">
      <h3 class="home-title text-[clamp(20px,2.5vw,28px)] font-bold">
        {{ t('whoSmallBiz') }}
      </h3>

      <p>{{ t('whoSmallBizText') }}</p>

      <h3 class="home-title text-[clamp(20px,2.5vw,28px)] font-bold">
        {{ t('whoGrowth') }}
      </h3>

      <p>{{ t('whoGrowthText') }}</p>

      <h3 class="home-title text-[clamp(20px,2.5vw,28px)] font-bold">
        {{ t('whoLarge') }}
      </h3>

      <p>{{ t('whoLargeText') }}</p>
    </sovi-home-card>

    <!-- Industries -->
    <sovi-home-card
      src="/images/content-14.webp"
      :title="t('industriesTitle')"
      flipped
    >
      <h4 class="font-bold">{{ t('industryRealEstate') }}</h4>

      <p>{{ t('industryRealEstateText') }}</p>

      <h4 class="font-bold">{{ t('industryConstruction') }}</h4>

      <p>{{ t('industryConstructionText') }}</p>

      <h4 class="font-bold">{{ t('industryHr') }}</h4>

      <p>{{ t('industryHrText') }}</p>

      <h4 class="font-bold">{{ t('industryLegal') }}</h4>

      <p>{{ t('industryLegalText') }}</p>

      <h4 class="font-bold">{{ t('industryHousing') }}</h4>

      <p>{{ t('industryHousingText') }}</p>
    </sovi-home-card>

    <!-- Why Sovi.fi -->
    <sovi-home-card src="/images/content-15.webp" :title="t('whySoviTitle')">
      <p>{{ t('whySoviIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('whySoviFeature1') }}</li>

        <li>{{ t('whySoviFeature2') }}</li>

        <li>{{ t('whySoviFeature3') }}</li>

        <li>{{ t('whySoviFeature4') }}</li>

        <li>{{ t('whySoviFeature5') }}</li>

        <li>{{ t('whySoviFeature6') }}</li>

        <li>{{ t('whySoviFeature7') }}</li>

        <li>{{ t('whySoviFeature8') }}</li>
      </ul>

      <p>{{ t('whySoviConclusion') }}</p>
    </sovi-home-card>

    <!-- Templates -->
    <sovi-home-card
      src="/images/content-16.webp"
      :title="t('templatesTitle')"
      flipped
    >
      <p>{{ t('templatesText') }}</p>

      <ul class="ms-6 list-disc">
        <li>
          <router-link to="/tyosopimuksen-sahkoinen-allekirjoitus">
            {{ t('templateEmployment') }}
          </router-link>
        </li>

        <li>{{ t('templateNda') }}</li>

        <li>
          <router-link to="/vuokrasopimuksen-sahkoinen-allekirjoitus">
            {{ t('templateRental') }}
          </router-link>
        </li>

        <li>{{ t('templateConsulting') }}</li>

        <li>{{ t('templateAssignment') }}</li>

        <li>{{ t('templateOffer') }}</li>
      </ul>

      <p>{{ t('templatesConclusion') }}</p>
    </sovi-home-card>

    <!-- CTA section -->
    <sovi-home-card
      src="/images/content-17.webp"
      :title="t('ctaTitle')"
    >
      <p>{{ t('ctaIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('ctaFeature1') }}</li>

        <li>{{ t('ctaFeature2') }}</li>

        <li>{{ t('ctaFeature3') }}</li>

        <li>{{ t('ctaFeature4') }}</li>

        <li>{{ t('ctaFeature5') }}</li>

        <li>{{ t('ctaFeature6') }}</li>
      </ul>

      <p>
        <strong>{{ t('ctaAction') }}</strong>
      </p>
    </sovi-home-card>

    <!-- Navigation links -->
    <div
      style="
        padding: clamp(32px, 5vw, 64px) clamp(16px, 5vw, 32px);
        background-color: #fff;
      "
    >
      <div style="display: grid; gap: 8px">
        <router-link to="/sahkoinen-allekirjoitus">
          {{ t('navMain') }} →
        </router-link>

        <router-link to="/sahkoinen-allekirjoitus-turvallisuus">
          {{ t('navSecurity') }} →
        </router-link>

        <router-link to="/ukk">{{ t('navFaq') }} →</router-link>
      </div>
    </div>

    <div class="sovi-esign-biz-page__login">
      <sovi-home-login />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { definePage } from 'vue-router/auto';
import { useRouteMeta } from '@/composables/useRouteMeta';
import { useAuthorizationStore } from '@/stores/authorization';
import { loadRecaptcha } from '@/plugins/recaptcha';
import SoviHomeLogin from '@/components/home/SoviHomeLogin.vue';

definePage({
  meta: {
    hero: false,
    restricted: false,
    maxContainer: true,
  },
});

useRouteMeta({
  title:
    'Sähköinen allekirjoitus yrityksille – Käyttökohteet ja hyödyt | Sovi.fi',
  description:
    'Sähköinen allekirjoitus sopii kaikkien toimialojen yrityksille. Tutustu käyttökohteisiin, toimialaesimerkkeihin ja asiakirjamalleihin.',
});

const { locale } = useI18n({ useScope: 'global' });
const { t } = useI18n();
const theme = useTheme();
const { isAuthorized } = useAuthorizationStore();

const onLocaleChange = (newLocale: string) => {
  locale.value = newLocale;
  localStorage.setItem('sovi-locale', newLocale);
};

onMounted(() => {
  document.getElementById('sovi-main')?.scrollTo(0, 0);
  window.scrollTo(0, 0);
  loadRecaptcha();

  const storedLocale = localStorage.getItem('sovi-locale');

  if (storedLocale && storedLocale !== locale.value) {
    locale.value = storedLocale;
  }

  theme.global.name.value = 'light';

  isAuthorized();
});
</script>

<style lang="scss">
.grecaptcha-badge {
  visibility: hidden;
}

.sovi-esign-biz-page {
  position: relative;
  background-color: #fff;
  font-size: clamp(18px, 2vw, 22px);

  &__logo {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 2;
  }

  .language-menu {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    box-shadow:
      rgba(0, 0, 0, 0.15) 0px 15px 25px,
      rgba(0, 0, 0, 0.05) 0px 5px 10px;
  }

  .v-table {
    max-width: 100%;
    overflow-x: auto;
    display: block;
    font-size: clamp(12px, 1.5vw, 16px);
  }

  .home-title {
    line-height: 1.3;

    @media (min-width: 768px) {
      line-height: 1.1;
    }
    --title-color: rgb(var(--v-theme-primary));

    &.alt {
      --title-color: #eca1c2;
    }

    &::first-letter {
      padding-bottom: 2px;
      box-shadow: inset 0 calc(-1 * clamp(4px, 0.5vw, 6px)) 0 0
        var(--title-color);
    }
  }

  &__login {
    overflow: hidden;

    .sovi-home__login {
      &::before,
      &::after {
        display: none;
      }
    }
    background-color: #fff;
    padding: clamp(32px, 5vw, 64px) clamp(16px, 5vw, 32px);

    .sovi-home__login-form {
      display: grid;
      gap: clamp(16px, 3vw, 32px);
      position: relative;
      z-index: 1;

      h1 {
        font-size: clamp(24px, 3vw, 32px);
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "fi": {
    "heroTitle": "Sähköinen allekirjoitus yrityksille",
    "heroSubtitle": "Käyttökohteet, toimialat ja asiakirjamallit",
    "heroText": "Sähköinen allekirjoitus soveltuu lähes kaikkiin yrityksen asiakirjoihin – työsopimuksista hankintasopimuksiin. Tutustu yleisimpiin käyttökohteisiin ja katso, miten Sovi.fi palvelee eri toimialojen yrityksiä.",
    "useCasesTitle": "Missä sähköistä allekirjoitusta voidaan käyttää?",
    "useCasesIntro": "Sähköinen allekirjoitus soveltuu lähes kaikkiin yrityksen asiakirjoihin. Yleisimpiä käyttökohteita ovat:",
    "useCaseEmployment": "työsopimukset",
    "useCaseRental": "vuokrasopimukset",
    "useCaseAssignment": "toimeksiantosopimukset",
    "useCaseOffers": "tarjoukset",
    "useCaseRfp": "tarjouspyynnöt",
    "useCaseConsulting": "konsulttisopimukset",
    "useCaseNda": "salassapitosopimukset eli NDA-sopimukset",
    "useCaseBoardMinutes": "hallituksen pöytäkirjat",
    "useCaseAgm": "yhtiökokousten asiakirjat",
    "useCaseProcurement": "hankintasopimukset",
    "useCasePartnership": "yhteistyösopimukset",
    "useCasePowerOfAttorney": "valtakirjat",
    "useCaseOrderConfirmation": "tilausvahvistukset",
    "useCaseService": "palvelusopimukset",
    "useCaseCustomer": "asiakassopimukset",
    "useCasePartner": "kumppanisopimukset",
    "useCasesConclusion": "Yhä useammat yritykset hyödyntävät sähköistä allekirjoitusta myös sisäisissä hyväksyntäprosesseissa, joissa nopeus ja dokumentoinnin luotettavuus ovat tärkeitä.",
    "whoTitle": "Kenelle sähköinen allekirjoitus sopii?",
    "whoSmallBiz": "Pienet yritykset",
    "whoSmallBizText": "Pienessä yrityksessä yrittäjä hoitaa usein itse myynnin, sopimukset, laskutuksen ja hallinnon. Jokainen säästetty tunti näkyy suoraan yrityksen kannattavuudessa. Kun sopimukset voidaan allekirjoittaa sähköisesti, aikaa jää enemmän asiakkaiden palvelemiseen ja liiketoiminnan kasvattamiseen.",
    "whoGrowth": "Kasvuyritykset",
    "whoGrowthText": "Kasvavat yritykset käsittelevät nopeasti kasvavan määrän sopimuksia. Manuaalinen paperityö muodostuu helposti pullonkaulaksi. Sähköinen allekirjoitus auttaa pitämään prosessit tehokkaina myös liiketoiminnan kasvaessa.",
    "whoLarge": "Suuret yritykset",
    "whoLargeText": "Suurissa organisaatioissa sopimuksia voi syntyä satoja tai tuhansia vuodessa. Kun allekirjoitusprosessi on sähköinen, voidaan säästää merkittävästi työaikaa sekä vähentää virheitä ja hallinnollista työtä.",
    "industriesTitle": "Esimerkkejä eri toimialoilta",
    "industryRealEstate": "Kiinteistönvälitys",
    "industryRealEstateText": "Kiinteistöalalla käsitellään jatkuvasti toimeksiantosopimuksia, vuokrasopimuksia, tarjouksia ja muita asiakirjoja. Sähköinen allekirjoitus mahdollistaa sen, että asiakas voi hyväksyä asiakirjat nopeasti myös etänä.",
    "industryConstruction": "Rakennusala",
    "industryConstructionText": "Rakennusyritykset allekirjoittavat urakkasopimuksia, aliurakoitsijasopimuksia, työmaakohtaisia dokumentteja ja vastaanottopöytäkirjoja. Nopea allekirjoitus vähentää projektien viivästyksiä.",
    "industryHr": "Henkilöstöpalvelut",
    "industryHrText": "Työsopimukset voidaan allekirjoittaa jo ennen ensimmäistä työpäivää. Työntekijä voi hyväksyä sopimuksen helposti omalla puhelimellaan.",
    "industryLegal": "Lakipalvelut",
    "industryLegalText": "Asianajotoimistot ja lakiasiaintoimistot käsittelevät suuren määrän sopimuksia ja valtakirjoja. Sähköinen allekirjoitus nopeuttaa asiakkaiden asiointia ja vähentää toimistokäyntejä.",
    "industryHousing": "Taloyhtiöt ja isännöinti",
    "industryHousingText": "Isännöitsijät ja taloyhtiöt voivat käyttää sähköistä allekirjoitusta esimerkiksi pöytäkirjoihin, urakkasopimuksiin ja muihin hallinnollisiin asiakirjoihin.",
    "whySoviTitle": "Miksi valita Sovi.fi?",
    "whySoviIntro": "Sähköisen allekirjoituspalvelun valinnassa kannattaa kiinnittää huomiota käytettävyyteen, hinnoitteluun ja siihen, kuinka hyvin palvelu tukee yrityksen arkea. Sovi.fi on suunniteltu erityisesti suomalaisille yrityksille. Palvelun keskeisiä etuja ovat:",
    "whySoviFeature1": "helppokäyttöinen käyttöliittymä",
    "whySoviFeature2": "nopea allekirjoitusprosessi",
    "whySoviFeature3": "asiakirjojen keskitetty hallinta",
    "whySoviFeature4": "asiakirjamallit toistuvaan käyttöön",
    "whySoviFeature5": "kansiorakenne dokumenttien organisointiin",
    "whySoviFeature6": "mahdollisuus seurata allekirjoitusten etenemistä",
    "whySoviFeature7": "turvallinen dokumenttien säilytys",
    "whySoviFeature8": "joustava hinnoittelu ilman pakollista kuukausimaksua",
    "whySoviConclusion": "Tämä tekee palvelusta erinomaisen vaihtoehdon sekä satunnaiseen että aktiiviseen käyttöön.",
    "templatesTitle": "Asiakirjamallit säästävät aikaa",
    "templatesText": "Monet yritykset käyttävät samoja sopimuspohjia päivittäin. Kun asiakirjamallit tallennetaan valmiiksi, uusia sopimuksia voidaan luoda muutamassa minuutissa ilman, että samoja tietoja tarvitsee kirjoittaa uudelleen. Esimerkiksi:",
    "templateEmployment": "työsopimus",
    "templateNda": "salassapitosopimus",
    "templateRental": "vuokrasopimus",
    "templateConsulting": "konsulttisopimus",
    "templateAssignment": "toimeksiantosopimus",
    "templateOffer": "tarjous",
    "templatesConclusion": "Tämä vähentää virheitä ja yhdenmukaistaa yrityksen sopimusprosessia.",
    "ctaTitle": "Aloita sähköinen allekirjoitus Sovi.fi:ssä",
    "ctaIntro": "Olipa kyse yksittäisestä sopimuksesta tai tuhansista asiakirjoista vuodessa, Sovi.fi tarjoaa helpon ja turvallisen ratkaisun sähköiseen allekirjoittamiseen. Sovi.fi:n avulla voit:",
    "ctaFeature1": "lähettää asiakirjoja allekirjoitettavaksi muutamassa minuutissa",
    "ctaFeature2": "seurata allekirjoitusprosessin etenemistä reaaliaikaisesti",
    "ctaFeature3": "käyttää valmiita asiakirjamalleja",
    "ctaFeature4": "hallita dokumentteja kansioissa",
    "ctaFeature5": "vähentää paperityötä ja manuaalisia työvaiheita",
    "ctaFeature6": "nopeuttaa sopimusprosesseja koko organisaatiossa",
    "ctaAction": "Luo maksuton tili ja lähetä ensimmäinen asiakirjasi allekirjoitettavaksi muutamassa minuutissa.",
    "navMain": "Sähköinen allekirjoitus",
    "navSecurity": "Turvallisuus ja juridinen pätevyys",
    "navFaq": "Usein kysytyt kysymykset"
  },
  "en": {
    "heroTitle": "Electronic Signatures for Businesses",
    "heroSubtitle": "Use cases, industries, and document templates",
    "heroText": "Electronic signatures are suitable for nearly all business documents — from employment contracts to procurement agreements. Explore the most common use cases and see how Sovi.fi serves businesses across different industries.",
    "useCasesTitle": "Where can electronic signatures be used?",
    "useCasesIntro": "Electronic signatures are suitable for nearly all business documents. The most common use cases include:",
    "useCaseEmployment": "employment contracts",
    "useCaseRental": "rental agreements",
    "useCaseAssignment": "assignment agreements",
    "useCaseOffers": "proposals and quotes",
    "useCaseRfp": "requests for proposals",
    "useCaseConsulting": "consulting agreements",
    "useCaseNda": "non-disclosure agreements (NDAs)",
    "useCaseBoardMinutes": "board meeting minutes",
    "useCaseAgm": "general meeting documents",
    "useCaseProcurement": "procurement contracts",
    "useCasePartnership": "partnership agreements",
    "useCasePowerOfAttorney": "powers of attorney",
    "useCaseOrderConfirmation": "order confirmations",
    "useCaseService": "service agreements",
    "useCaseCustomer": "customer agreements",
    "useCasePartner": "partner agreements",
    "useCasesConclusion": "More and more businesses are also using electronic signatures for internal approval processes where speed and reliable documentation are essential.",
    "whoTitle": "Who are electronic signatures for?",
    "whoSmallBiz": "Small businesses",
    "whoSmallBizText": "In a small business, the entrepreneur often handles sales, contracts, invoicing, and administration alone. Every hour saved directly impacts the company's profitability. When contracts can be signed electronically, more time is available for serving customers and growing the business.",
    "whoGrowth": "Growing companies",
    "whoGrowthText": "Growing companies handle a rapidly increasing volume of contracts. Manual paperwork easily becomes a bottleneck. Electronic signatures help keep processes efficient as the business scales.",
    "whoLarge": "Large organizations",
    "whoLargeText": "In large organizations, hundreds or thousands of contracts may be created each year. When the signing process is electronic, significant working time can be saved while reducing errors and administrative work.",
    "industriesTitle": "Industry examples",
    "industryRealEstate": "Real estate",
    "industryRealEstateText": "The real estate industry constantly handles assignment agreements, rental contracts, offers, and other documents. Electronic signatures allow clients to approve documents quickly, even remotely.",
    "industryConstruction": "Construction",
    "industryConstructionText": "Construction companies sign project contracts, subcontractor agreements, site-specific documents, and acceptance reports. Fast signing reduces project delays.",
    "industryHr": "HR services",
    "industryHrText": "Employment contracts can be signed before the first day of work. Employees can easily accept agreements on their own phone.",
    "industryLegal": "Legal services",
    "industryLegalText": "Law firms handle large volumes of contracts and powers of attorney. Electronic signatures speed up client interactions and reduce office visits.",
    "industryHousing": "Housing companies and property management",
    "industryHousingText": "Property managers and housing companies can use electronic signatures for meeting minutes, project contracts, and other administrative documents.",
    "whySoviTitle": "Why choose Sovi.fi?",
    "whySoviIntro": "When choosing an electronic signature service, it's worth considering usability, pricing, and how well the service supports your daily operations. Sovi.fi is designed specifically for Finnish businesses. Key advantages include:",
    "whySoviFeature1": "easy-to-use interface",
    "whySoviFeature2": "fast signing process",
    "whySoviFeature3": "centralized document management",
    "whySoviFeature4": "document templates for recurring use",
    "whySoviFeature5": "folder structure for organizing documents",
    "whySoviFeature6": "ability to track signing progress",
    "whySoviFeature7": "secure document storage",
    "whySoviFeature8": "flexible pricing with no mandatory monthly fee",
    "whySoviConclusion": "This makes the service an excellent option for both occasional and frequent use.",
    "templatesTitle": "Document templates save time",
    "templatesText": "Many businesses use the same contract templates daily. When document templates are saved in advance, new contracts can be created in minutes without re-entering the same information. For example:",
    "templateEmployment": "employment contract",
    "templateNda": "non-disclosure agreement",
    "templateRental": "rental agreement",
    "templateConsulting": "consulting agreement",
    "templateAssignment": "assignment agreement",
    "templateOffer": "proposal",
    "templatesConclusion": "This reduces errors and standardizes the company's contracting process.",
    "ctaTitle": "Start signing electronically with Sovi.fi",
    "ctaIntro": "Whether it's a single contract or thousands of documents per year, Sovi.fi offers an easy and secure solution for electronic signing. With Sovi.fi you can:",
    "ctaFeature1": "send documents for signing in minutes",
    "ctaFeature2": "track signing progress in real time",
    "ctaFeature3": "use ready-made document templates",
    "ctaFeature4": "manage documents in folders",
    "ctaFeature5": "reduce paperwork and manual steps",
    "ctaFeature6": "speed up contracting processes across the organization",
    "ctaAction": "Create a free account and send your first document for signing in just a few minutes.",
    "navMain": "Electronic signatures",
    "navSecurity": "Security and legal validity",
    "navFaq": "Frequently asked questions"
  }
}
</i18n>
